import * as api from "./api";

/**
 * Screenshot attachments, prepared in the browser.
 *
 * A tester's evidence is a screen grab — routinely a 4–8 MB PNG straight off a
 * retina display. Uploading that verbatim would put megabytes into Postgres per
 * note and make the panel feel broken on a hotel wifi, so the image is
 * downscaled and re-encoded HERE, before it ever reaches the wire. The server
 * still enforces its own ceiling: this is a courtesy, not a guarantee.
 *
 * No framework and no runtime dependency, like the rest of `widget/**` — canvas
 * and `createImageBitmap` are the whole toolchain.
 *
 * Fetched bytes become object URLs held in a module-level cache. The panel
 * re-renders its whole body on every state change, so a thumbnail has to be
 * cheap to re-create; without the cache each render would re-download every
 * screenshot on screen.
 */

/** Longest edge of a stored image. Enough to read a form field in a screenshot,
 *  which is what an attachment is for. */
const MAX_EDGE = 1600;
/** What the encoder aims for; it steps quality down until it fits. */
const TARGET_BYTES = 900_000;
const QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4, 0.3];

const urls = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

export interface PreparedImage {
  base64: string;
  mime: "image/webp" | "image/jpeg";
  width: number;
  height: number;
  blob: Blob;
}

// ------------------------------------------------------------------ decode --

async function decode(
  file: Blob,
): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height };
  }
  // Older Safari. The object URL is revoked once the pixels are on the canvas.
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Could not read that image"));
      element.src = url;
    });
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(
  canvas: HTMLCanvasElement,
  mime: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
}

async function encode(
  canvas: HTMLCanvasElement,
): Promise<{ blob: Blob; mime: PreparedImage["mime"] }> {
  for (const mime of ["image/webp", "image/jpeg"] as const) {
    let smallest: Blob | null = null;
    for (const quality of QUALITY_STEPS) {
      const blob = await toBlob(canvas, mime, quality);
      // A browser that cannot encode this type silently hands back a PNG
      // instead of failing — which is both enormous and not what was asked
      // for, so treat a type mismatch as "unsupported" and move on.
      if (!blob || blob.type !== mime) break;
      if (blob.size <= TARGET_BYTES) return { blob, mime };
      smallest = blob;
    }
    if (smallest) return { blob: smallest, mime };
  }
  throw new Error("This browser could not re-encode that image");
}

function base64Of(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  // Chunked: `String.fromCharCode(...bytes)` on a megabyte blows the argument
  // limit and throws where a plain loop would merely be slow.
  const CHUNK = 0x8000;
  let binary = "";
  for (let index = 0; index < bytes.length; index += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(index, index + CHUNK));
  }
  return btoa(binary);
}

/** Downscale + re-encode, ready to upload. */
export async function prepareImage(file: Blob): Promise<PreparedImage> {
  const { source, width, height } = await decode(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser could not process that image");
  // A screenshot of a light UI re-encoded as JPEG would otherwise get a black
  // background wherever the source had transparency.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, targetWidth, targetHeight);
  context.drawImage(source, 0, 0, targetWidth, targetHeight);
  if (typeof ImageBitmap !== "undefined" && source instanceof ImageBitmap) {
    source.close();
  }

  const { blob, mime } = await encode(canvas);
  return {
    base64: base64Of(await blob.arrayBuffer()),
    mime,
    width: targetWidth,
    height: targetHeight,
    blob,
  };
}

// ------------------------------------------------------------------ upload --

/**
 * Prepare and upload one image; resolves to the attachment id the finding or
 * checklist note should claim. The local bytes are cached against that id, so
 * the thumbnail appears without a round-trip back for what we just sent.
 */
export async function uploadImage(
  file: Blob,
  sessionId: string,
): Promise<string> {
  const prepared = await prepareImage(file);
  const attachment = await api.createAttachment({
    session_id: sessionId,
    mime: prepared.mime,
    width: prepared.width,
    height: prepared.height,
    data: prepared.base64,
  });
  urls.set(attachment.id, URL.createObjectURL(prepared.blob));
  return attachment.id;
}

// ------------------------------------------------------------------- cache --

/** The object URL for an attachment, if it is already in hand. */
export function attachmentUrl(id: string): string | null {
  return urls.get(id) ?? null;
}

/**
 * Fetch an attachment's bytes and cache them. Null on failure — a screenshot
 * that will not load is a broken thumbnail, never a broken panel.
 *
 * Deduplicated on the id because several renders can ask for the same image
 * before the first response lands.
 */
export function loadAttachment(id: string): Promise<string | null> {
  const cached = urls.get(id);
  if (cached) return Promise.resolve(cached);
  const existing = inflight.get(id);
  if (existing) return existing;

  const request = api
    .fetchAttachment(id)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      urls.set(id, url);
      return url;
    })
    .catch(() => null)
    .finally(() => inflight.delete(id));

  inflight.set(id, request);
  return request;
}
